import { type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowRight,
  AudioLines,
  BookOpen,
  Brain,
  Code2,
  Eye,
  FileText,
  Image as ImageIcon,
  Languages,
  MessageSquare,
  Mic,
  Video,
  Volume2,
} from 'lucide-react'
import { Reveal } from '@/components/landing/Reveal'
import { PageOutro, StatusChip, VisionSection, type Status } from '@/components/landing/Section'

/** The Features page: every kind of model NevTan AI means to run. */
export function FeaturesPage() {
  return (
    <>
      <Modalities />
      <PageOutro>
        <h2 className="nv-h2 text-[1.6rem] sm:text-[2rem]">Run these on your own machine</h2>
        <p className="mx-auto mt-3 max-w-xl text-[15px] leading-relaxed text-brand-body">
          Install the app, let it check your hardware, and start with what works today.

        </p>
        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
          <Link to="/download" className="nv-btn-primary-lg">
            Download NevTan AI <ArrowRight size={16} />
          </Link>
          <Link to="/roadmap" className="nv-btn-outline-lg">
            See what ships today
          </Link>
        </div>
      </PageOutro>
    </>
  )
}

const MODALITIES: {
  icon: ReactNode
  title: string
  body: string
  models: string
  status: Status
}[] = [
  {
    icon: <MessageSquare size={18} />,
    title: 'Chat & general',
    body: 'Ask questions, draft and edit writing, summarize text, and get everyday help.',
    models: 'Qwen · Llama · Gemma · Mistral · Phi',
    status: 'today',
  },
  {
    icon: <Code2 size={18} />,
    title: 'Coding',
    body: 'Write, explain, debug, and improve code. Whole-project help is planned.',
    models: 'Qwen Coder · DeepSeek Coder · Devstral · CodeGemma',
    status: 'today',
  },
  {
    icon: <Brain size={18} />,
    title: 'Reasoning',
    body: 'Models built for math, planning, and problems that need step-by-step thinking.',
    models: 'DeepSeek R1 · Qwen reasoning models',
    status: 'planned',
  },
  {
    icon: <Eye size={18} />,
    title: 'Vision',
    body: 'Drop in a screenshot, chart, or scan and ask about it. Answered on your machine.',
    models: 'Qwen-VL · Llama Vision · LLaVA',
    status: 'planned',
  },
  {
    icon: <FileText size={18} />,
    title: 'Documents & OCR',
    body: 'Read PDFs, pull data out of tables, and turn scans into text.',
    models: 'PaddleOCR · Surya · Nougat',
    status: 'planned',
  },
  {
    icon: <ImageIcon size={18} />,
    title: 'Image generation',
    body: 'Describe an image and generate it locally. No credits to buy.',
    models: 'FLUX · Stable Diffusion · SDXL',
    status: 'planned',
  },
  {
    icon: <Mic size={18} />,
    title: 'Speech to text',
    body: 'Talk instead of typing, and turn recordings and meetings into text.',
    models: 'Whisper · Distil-Whisper · Parakeet',
    status: 'planned',
  },
  {
    icon: <Volume2 size={18} />,
    title: 'Text to speech',
    body: 'Have answers read out loud in a voice generated on your machine.',
    models: 'Piper · Kokoro · XTTS',
    status: 'planned',
  },
  {
    icon: <Languages size={18} />,
    title: 'Translation',
    body: 'Translate between languages without sending your text anywhere.',
    models: 'NLLB · SeamlessM4T · MADLAD',
    status: 'planned',
  },
  {
    icon: <AudioLines size={18} />,
    title: 'Audio & music',
    body: 'Generate music and sound effects, and analyze audio you already have.',
    models: 'MusicGen · AudioGen · Stable Audio',
    status: 'planned',
  },
  {
    icon: <Video size={18} />,
    title: 'Video',
    body: 'Turn text and images into short video clips as hardware allows.',
    models: 'Wan · LTX-Video · CogVideoX',
    status: 'planned',
  },
  {
    icon: <BookOpen size={18} />,
    title: 'Your knowledge',
    body: 'Point NevTan AI at your own files and ask questions about them.',
    models: 'BGE-M3 · E5 · Nomic Embed',
    status: 'planned',
  },
]

function Modalities() {
  return (
    <VisionSection
      id="features"
      eyebrow="One platform"
      title="Every kind of model, in one place"
      lede="One app for every kind of model, instead of a separate tool for each. Chat and coding work today. The rest are on the roadmap."
    >
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {MODALITIES.map((item, index) => (
          <Reveal key={item.title} delay={(index % 3) * 70}>
            <div className="flex h-full flex-col rounded-xl border border-brand-line bg-brand-paper p-5 shadow-card transition hover:-translate-y-0.5 hover:shadow-raised">
              <div className="flex items-start justify-between gap-2">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-tint text-brand-tint-ink">
                  {item.icon}
                </span>
                <StatusChip status={item.status} />
              </div>
              <h3 className="mt-3.5 text-[15px] font-bold text-brand-heading">{item.title}</h3>
              <p className="mt-1.5 flex-1 text-[13.5px] leading-relaxed text-brand-muted">
                {item.body}
              </p>
              <p className="mt-3 border-t border-brand-line pt-2.5 text-[11px] text-brand-muted">
                {item.models}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </VisionSection>
  )
}
